import React, { useState } from 'react';
import '@/App.css';
import axios from 'axios';
import { Menu, X, ArrowRight, CheckCircle, Users, TrendingUp, Target, Zap, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      setTimeout(() => setFormStatus(''), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 5000);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const services = [
    {
      icon: <Target className="w-12 h-12" />,
      title: 'Performance Marketing',
      description: 'Drive rapid, measurable growth across your entire digital ecosystem with data-driven campaigns.'
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Brand Strategy',
      description: 'Build a powerful brand identity that resonates with your audience and stands out in the market.'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Social Media Marketing',
      description: 'Engage your audience and grow your community across all major social platforms.'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'SEO & SEM',
      description: 'Increase your visibility and drive qualified traffic with our expert search marketing strategies.'
    },
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: 'Content Creation',
      description: 'Compelling content that tells your story and converts prospects into loyal customers.'
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: 'AdTech Solutions',
      description: 'Leverage cutting-edge advertising technology to optimize your campaigns and maximize ROI.'
    }
  ];

  const benefits = [
    {
      number: '01',
      title: 'Data-Driven Approach',
      description: 'Every decision backed by real-time analytics and performance metrics.'
    },
    {
      number: '02',
      title: 'Proven Expertise',
      description: 'Years of experience delivering results across diverse industries and markets.'
    },
    {
      number: '03',
      title: 'Strategic Execution',
      description: 'Strategy-first approach combined with flawless implementation.'
    },
    {
      number: '04',
      title: 'Scalable Solutions',
      description: 'Growth strategies designed to scale with your business objectives.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO, TechVision',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop',
      text: 'Rivads transformed our digital presence completely. Their strategic approach helped us achieve 300% growth in just 6 months.'
    },
    {
      name: 'Michael Chen',
      role: 'Marketing Director, InnovateCo',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
      text: 'Working with Rivads was a game-changer. Their expertise in performance marketing delivered results beyond our expectations.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Founder, BrightPath',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop',
      text: 'The team at Rivads truly understands modern marketing. Our ROI improved dramatically within the first quarter.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Campaigns Delivered' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '5M+', label: 'Audience Reached' },
    { number: '10+', label: 'Years Experience' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm z-50 shadow-sm" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-testid="logo">
                RIVADS
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-700 hover:text-blue-600 transition" data-testid="nav-home">Home</a>
              <a href="#about" className="text-gray-700 hover:text-blue-600 transition" data-testid="nav-about">About</a>
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition" data-testid="nav-services">Services</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition" data-testid="nav-testimonials">Testimonials</a>
              <a href="#contact" className="bg-blue-600 text-white px-6 py-2.5 rounded-full hover:bg-blue-700 transition" data-testid="nav-contact-btn">
                Get Started
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <a href="#home" className="block text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Home</a>
              <a href="#about" className="block text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#services" className="block text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#testimonials" className="block text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
              <a href="#contact" className="block bg-blue-600 text-white px-6 py-2.5 rounded-full text-center" onClick={() => setMobileMenuOpen(false)}>
                Get Started
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-purple-50" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-6" data-testid="hero-badge">
                Welcome to Rivads
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-testid="hero-heading">
                Elevate Your Brand with 
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Data-Driven </span>
                Marketing Solutions
              </h1>
              <p className="text-xl text-gray-600 mb-8" data-testid="hero-description">
                Partner with Rivads for intelligent advertising solutions that captivate your audience, accelerate growth, and ensure lasting success across every digital touchpoint.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-700 transition" data-testid="hero-cta-primary">
                  Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a href="#services" className="inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full text-lg font-semibold hover:border-blue-600 hover:text-blue-600 transition" data-testid="hero-cta-secondary">
                  Our Services
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?w=800&h=600&fit=crop"
                alt="Professional marketing team collaboration"
                className="rounded-2xl shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold text-blue-600">500+</p>
                <p className="text-gray-600">Successful Campaigns</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-600" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</p>
                <p className="text-blue-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
                data-testid="about-image"
              />
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4" data-testid="about-badge">
                About Rivads
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" data-testid="about-heading">
                Your Strategic Partner in Scalable Advertising Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-testid="about-description">
                At Rivads, we're more than just a marketing agency – we're your dedicated growth engine. Our expertise lies at the intersection of market intelligence, advertising technology, and performance marketing.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We partner with brands and technology companies to design, validate, and execute high-impact marketing strategies that deliver measurable results. Our data-driven approach ensures every campaign is optimized for maximum efficiency and ROI.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Strategic Excellence</h3>
                  <p className="text-gray-600 text-sm">Proven strategies that deliver consistent results</p>
                </div>
                <div>
                  <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Data Intelligence</h3>
                  <p className="text-gray-600 text-sm">Insights-driven decisions for optimal performance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4" data-testid="services-badge">
              Our Solutions
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="services-heading">
              Digital Solutions That Drive Real Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-testid="services-description">
              From strategy to execution, we deliver comprehensive marketing solutions tailored to your business goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-blue-600 mb-4 group-hover:scale-110 transition">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <a href="#contact" className="text-blue-600 font-semibold inline-flex items-center hover:gap-2 transition-all">
                  Learn More <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="benefits-heading">
              Why Choose Rivads?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process helps brands scale faster with smart strategy, precise execution, and measurable results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="relative" data-testid={`benefit-${index}`}>
                <div className="text-6xl font-bold text-blue-100 mb-4">{benefit.number}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4" data-testid="testimonials-badge">
              Client Success Stories
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="testimonials-heading">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">Real results from real partnerships</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg" data-testid={`testimonial-${index}`}>
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-testid="contact-heading">
                Ready to Accelerate Your Growth?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let's discuss how Rivads can help transform your marketing strategy and drive measurable results.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-blue-400 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Email Us</h4>
                    <a href="mailto:sales@rivads.com" className="text-gray-300 hover:text-blue-400 transition">
                      sales@rivads.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="w-6 h-6 text-blue-400 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Call Us</h4>
                    <a href="tel:+918287630181" className="text-gray-300 hover:text-blue-400 transition">
                      +91 8287630181
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl" data-testid="contact-form">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    placeholder="john@company.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    placeholder="Your Company"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    placeholder="Tell us about your project..."
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center font-medium" data-testid="contact-success-message">
                    ✓ Message sent successfully! We'll get back to you soon.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center font-medium" data-testid="contact-error-message">
                    ✗ Failed to send message. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">RIVADS</h3>
              <p className="text-sm">
                Elevating brands with data-driven marketing solutions and strategic excellence.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="hover:text-white transition">Performance Marketing</a></li>
                <li><a href="#services" className="hover:text-white transition">Brand Strategy</a></li>
                <li><a href="#services" className="hover:text-white transition">Social Media</a></li>
                <li><a href="#services" className="hover:text-white transition">SEO & SEM</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="hover:text-white transition">About Us</a></li>
                <li><a href="#testimonials" className="hover:text-white transition">Testimonials</a></li>
                <li><a href="#contact" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:sales@rivads.com" className="hover:text-white transition">sales@rivads.com</a></li>
                <li><a href="tel:+918287630181" className="hover:text-white transition">+91 8287630181</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© 2025 Rivads. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;